import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withEDSContext } from '../../EDSContext/EDSContext';
import { default as withStyles } from '../../styles';
import { Button, Popper, DropdownTypeahead, DropdownTypeaheadItem } from '@ellucian/react-design-system/core';
import { spacing40 } from '../../styles/tokens';
var styles = function styles(theme) {
  return {
    contentPadding: {
      paddingBottom: spacing40
    },
    paper: {
      width: theme.spacing(100) // 400 px
    },
    popperTitle: {
      fontSize: theme.typography.h4.fontSize,
      fontWeight: theme.typography.h4.fontWeight,
      marginBottom: spacing40
    },
    popperActions: {
      textAlign: 'right',
      '& button:not(:last-child)': {
        marginRight: spacing40
      }
    }
  };
};
var SubstitutionVariablesPopper = function SubstitutionVariablesPopper(props) {
  var _containerRef$current, _containerRef$current2;
  var classes = props.classes,
    edsContext = props.edsContext,
    variable = props.variable,
    substitutionVariableList = props.substitutionVariableList,
    isPopperOpen = props.isPopperOpen,
    _props$isEdit = props.isEdit,
    isEdit = _props$isEdit === void 0 ? false : _props$isEdit,
    onInsertSubstitutionVariable = props.onInsertSubstitutionVariable,
    onClose = props.onClose,
    containerRef = props.containerRef;
  var querySelector = 'button.ql-substitutionVariables';
  var _useState = useState(''),
    substitutionVariableError = _useState[0],
    setSubstitutionVariableError = _useState[1];
  var _useState2 = useState({
      variable: variable != null ? variable : ''
    }),
    substitutionVariable = _useState2[0],
    setSubstitutionVariable = _useState2[1];
  var _useState3 = useState((_containerRef$current = containerRef == null || (_containerRef$current2 = containerRef.current) == null ? void 0 : _containerRef$current2.querySelector(querySelector)) != null ? _containerRef$current : document.querySelector(querySelector)),
    substitutionVariablePopperAnchor = _useState3[0],
    setSubstitutionVariablePopperAnchor = _useState3[1];
  var variableDataInvalidMessage = edsContext.formatMessage('component.RichTextEditor.invalidVariableData');
  var variableDataAdd = edsContext.formatMessage('component.RichTextEditor.variableDataAdd');
  var variableDataEdit = edsContext.formatMessage('component.RichTextEditor.variableDataEdit');
  var variableDataFieldLabel = edsContext.formatMessage('component.RichTextEditor.variableDataFieldLabel');
  var saveButtonRef = useRef();
  var dropdownItems = useMemo(function () {
    return substitutionVariableList.map(function (item) {
      return /*#__PURE__*/React.createElement(DropdownTypeaheadItem, {
        key: item.value,
        value: item.value,
        label: item.label
      });
    });
  }, [substitutionVariableList]);
  var findSelectedValue = useCallback(function (variable) {
    var _dropdownItems$find;
    return ((_dropdownItems$find = dropdownItems.find(function (x) {
      return x.key === variable;
    })) == null ? void 0 : _dropdownItems$find.key) || null;
  }, [dropdownItems]);
  useEffect(function () {
    if (isPopperOpen) {
      setSubstitutionVariableError('');
    }
  }, [isPopperOpen]);
  useEffect(function () {
    var _containerRef$current3, _containerRef$current4;
    setSubstitutionVariable({
      variable: findSelectedValue(variable)
    });
    setSubstitutionVariablePopperAnchor((_containerRef$current3 = containerRef == null || (_containerRef$current4 = containerRef.current) == null ? void 0 : _containerRef$current4.querySelector(querySelector)) != null ? _containerRef$current3 : document.querySelector(querySelector));
  }, [containerRef, variable, substitutionVariableList, findSelectedValue]);

  // fires the onInsertSubstitutionVariable prop function and closes the popper if variable is present
  var handleInsertSubstitutionVariable = useCallback(function () {
    //validate that there is a variable selected
    if (substitutionVariable.variable && substitutionVariable.variable.length > 0) {
      onInsertSubstitutionVariable({
        variable: substitutionVariable.variable.trim()
      });
      setSubstitutionVariable({
        variable: ''
      });
    } else {
      setSubstitutionVariableError(variableDataInvalidMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [substitutionVariable, onInsertSubstitutionVariable, substitutionVariableError]);
  var handleSubstitutionVariableChange = useCallback(function (targetValue) {
    setSubstitutionVariable(function (prevSubstitutionVariable) {
      return _extends({}, prevSubstitutionVariable, {
        variable: targetValue
      });
    });
    if (targetValue && targetValue.length > 0) {
      setSubstitutionVariableError('');
    }
  }, []);
  var handleCancelSubstitutionVariable = useCallback(function () {
    onClose();
    setSubstitutionVariable({
      variable: ''
    });
  }, [onClose]);

  // traps and wraps the focus within the popper
  var wrapFocus = function wrapFocus(e) {
    // note: using an id lookup (instead of a ref) because it's not currently
    // possible to pass a ref to DropdownTypeahead without breaking its
    // underlying Search component
    var dropdown = document.getElementById('substitutionVariablesPopperPlaceholder');

    // ignore key-presses that are not tabs
    if (!(e.key === 'Tab')) {
      return;
    }
    if (document.activeElement === saveButtonRef.current && !e.shiftKey) {
      // focus start input
      e.preventDefault();
      dropdown.focus();
    }
    if (document.activeElement === dropdown && e.shiftKey) {
      // focus save button
      e.preventDefault();
      saveButtonRef.current.focus();
    }
  };

  // allows the user to press escape to close the popper
  var escapePopper = function escapePopper(e) {
    if (e.key === 'Escape') {
      handleCancelSubstitutionVariable();
    }
  };
  return /*#__PURE__*/React.createElement(Popper, {
    id: "RTE_substitutionVariablesPopper",
    role: "alertdialog",
    open: isPopperOpen,
    onClickAway: handleCancelSubstitutionVariable,
    onClose: onClose,
    classes: {
      paperRoot: classes.paper
    },
    title: isEdit ? variableDataEdit : variableDataAdd,
    TitleTypographyProps: {
      classes: {
        root: classes.popperTitle
      }
    },
    anchorEl: substitutionVariablePopperAnchor,
    placement: edsContext.dir === 'ltr' ? 'bottom-start' : 'bottom-end',
    onKeyDown: function onKeyDown(e) {
      return escapePopper(e);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.contentPadding
  }, /*#__PURE__*/React.createElement(DropdownTypeahead, {
    id: "substitutionVariablesPopperPlaceholder",
    required: true,
    value: findSelectedValue(substitutionVariable.variable),
    label: variableDataFieldLabel,
    onChange: handleSubstitutionVariableChange,
    error: substitutionVariableError !== '',
    helperText: substitutionVariableError,
    fullWidth: true
    // eslint-disable-next-line jsx-a11y/no-autofocus
    ,
    autoFocus: true,
    onKeyDown: function onKeyDown(e) {
      return wrapFocus(e);
    }
  }, dropdownItems)), /*#__PURE__*/React.createElement("div", {
    className: classes.popperActions
  }, /*#__PURE__*/React.createElement(Button, {
    "data-id": "substitutionVariablesPopperCancel",
    onClick: handleCancelSubstitutionVariable,
    color: "secondary",
    "aria-label": edsContext.formatMessage('component.RichTextEditor.cancel')
  }, edsContext.formatMessage('component.RichTextEditor.cancel')), /*#__PURE__*/React.createElement(Button, {
    "data-id": "substitutionVariablesPopperSave",
    onClick: handleInsertSubstitutionVariable,
    color: "primary",
    "aria-label": edsContext.formatMessage('component.RichTextEditor.save'),
    onKeyDown: function onKeyDown(e) {
      return wrapFocus(e);
    },
    ref: saveButtonRef
  }, edsContext.formatMessage('component.RichTextEditor.save'))));
};
SubstitutionVariablesPopper.propTypes = {
  /**
   * @ignore
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  edsContext: PropTypes.object,
  /**
   * Ref to the parent RichTextEditor container.
   * @ignore
  */
  containerRef: PropTypes.object,
  /**
  * Initial value for substitutionVariable variable
  */
  variable: PropTypes.string,
  /**
  * The list of key/value pairs to add to the variable list
  */
  substitutionVariableList: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  }).isRequired),
  /**
  * Indicates if the Popper should open or close
  */
  isPopperOpen: PropTypes.bool,
  /**
  * Indicates if the Popper's title should be edit or add
  */
  isEdit: PropTypes.bool,
  /**
  * Callback fired when the save button is clicked
  * `@param {object} substitutionVariable`   The substitutionVariable
  *
  */
  onInsertSubstitutionVariable: PropTypes.func.isRequired,
  /**
   * Callback fired when the cancel button is clicked
   */
  onClose: PropTypes.func.isRequired
};
export default withEDSContext(withStyles(styles)(SubstitutionVariablesPopper));